import { AlertTriangle, ArrowRight, CheckCircle2, Info, Loader2, Pause, Play, Zap } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/cn';

export interface PausedCronInfo {
  jobname: string;
  label: string;
  impact_when_paused: string | null;
  active: boolean;
  on_demand?: boolean;
  /** Inactive BY DESIGN. Stated as information rather than warned about — there is nothing to fix. */
  expected_paused?: boolean;
  expected_paused_reason?: string | null;
  min_on_demand_interval_minutes?: number;
  last_on_demand_run_at?: string | null;
  last_on_demand_status?: 'ok' | 'error' | null;
  queue_id?: string | null;
  queue_status?: 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped' | null;
  queued_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  queue_error?: string | null;
}

export interface CronStatusBannerProps {
  /** Paused or on-demand crons whose `depends_on_page_paths` include this page. */
  pausedJobs: PausedCronInfo[] | undefined;
  /** Path to the cron-jobs admin page (where the user can re-enable). */
  settingsHref?: string;
  /**
   * Callback that queues a cron job by name. The RPC stays named
   * `admin_run_cron_now` for compatibility, but it should return after
   * queueing the background run.
   */
  onRunNow?: (
    jobname: string,
    onDemandOnly: boolean,
  ) => Promise<{ ok: boolean; skipped?: boolean; error?: string } | void>;
  className?: string;
}

/**
 * Warning banner that appears on pages whose data feed depends on a
 * pg_cron job that's currently paused or in on-demand mode.
 *
 * - For paused jobs: shows a Sync now button per job so the
 *   operator can queue a background run without leaving the page.
 * - For on_demand jobs: shows queue/freshness state but does not
 *   auto-fire. Page views should never start heavy sync work.
 *
 * Renders nothing when every relevant cron is active and not
 * on-demand.
 */
export function CronStatusBanner({
  pausedJobs,
  settingsHref = '/admin/crons',
  onRunNow,
  className,
}: CronStatusBannerProps) {
  const [runState, setRunState] = useState<Record<string, 'idle' | 'running' | 'ok' | 'error'>>({});

  if (!pausedJobs || pausedJobs.length === 0) return null;

  // The headline used to read "needs attention" for every job in this list, next to a green
  // "succeeded" pill showing the last manual run. On the Journeys page that produced a permanent
  // warning saying a job had both succeeded and needed attention, for a cron that is switched off
  // deliberately. A banner that cries wolf on a healthy page teaches operators to ignore it, so
  // the headline now describes the worst state actually present.
  const failing = pausedJobs.filter((j) => j.queue_status === 'failed' || Boolean(j.queue_error));
  // Off BY DESIGN is not a fault and must not drive severity. Warning about something the reader
  // cannot resolve is how a banner trains people to ignore banners — and this one had been
  // sitting amber on the Journeys page for a runtime that was never meant to be on yet.
  const byDesign = pausedJobs.filter((j) => j.expected_paused && !failing.includes(j));
  const paused = pausedJobs.filter(
    (j) => !j.active && !j.on_demand && !j.expected_paused && !failing.includes(j),
  );
  const severity: 'failing' | 'paused' | 'on_demand' | 'by_design' =
    failing.length > 0
      ? 'failing'
      : paused.length > 0
        ? 'paused'
        : byDesign.length === pausedJobs.length
          ? 'by_design'
          : 'on_demand';

  const n = pausedJobs.length;
  const plural = n === 1 ? 'job' : 'jobs';
  const headline =
    severity === 'by_design'
      ? `${byDesign.length === 1 ? 'One background job' : `${byDesign.length} background jobs`} behind this page ${byDesign.length === 1 ? 'is' : 'are'} not switched on yet.`
      : severity === 'failing'
      ? `${failing.length} background data ${failing.length === 1 ? 'job' : 'jobs'} this page depends on ${failing.length === 1 ? 'is' : 'are'} failing.`
      : severity === 'paused'
        ? `${paused.length} background data ${paused.length === 1 ? 'job' : 'jobs'} this page depends on ${paused.length === 1 ? 'is' : 'are'} paused, so this data does not refresh on its own.`
        : `${n} background data ${plural} this page depends on ${n === 1 ? 'runs' : 'run'} on demand.`;

  async function handleRunNow(jobname: string) {
    if (!onRunNow) return;
    setRunState((s) => ({ ...s, [jobname]: 'running' }));
    try {
      const r = await onRunNow(jobname, false);
      const ok = !r || (typeof r === 'object' && r.ok !== false);
      setRunState((s) => ({ ...s, [jobname]: ok ? 'ok' : 'error' }));
    } catch {
      setRunState((s) => ({ ...s, [jobname]: 'error' }));
    }
  }

  return (
    <section
      className={cn(
        'text-body-sm flex flex-col gap-2 rounded-lg border px-4 py-3 text-text-primary',
        severity === 'failing'
          ? 'border-danger/40 bg-danger/10'
          : severity === 'paused'
            ? 'border-warning/40 bg-warning-soft'
            : 'border-border bg-bg-subtle',
        className,
      )}
      role="status"
    >
      <div className="flex items-start gap-2">
        {severity === 'failing' ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
        ) : severity === 'paused' ? (
          <Pause className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        ) : severity === 'by_design' ? (
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" aria-hidden />
        ) : (
          <Play className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium">{headline}</p>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {pausedJobs.map((j) => {
              const state = runState[j.jobname] ?? 'idle';
              return (
                <li key={j.jobname} className="flex flex-wrap items-start gap-2">
                  {j.on_demand ? (
                    <Play className="mt-0.5 h-3 w-3 shrink-0 text-accent-500" aria-hidden />
                  ) : (
                    <Pause className="mt-0.5 h-3 w-3 shrink-0 text-text-muted" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-text-primary">{j.label}</span>
                    {j.on_demand && (
                      <span className="border-accent-500/30 bg-accent-500/10 ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium text-accent-500">
                        Background sync
                      </span>
                    )}
                    {/* Suppressed for a job that is off by design. The pill reports the last
                        MANUAL run, so a stale green "succeeded" rendered directly before "not
                        switched on yet" — the same succeeded-and-broken contradiction this banner
                        was already fixed for once, just relocated. Caught on production. */}
                    {j.queue_status && !j.expected_paused && (
                      <span
                        className={cn(
                          'ml-1.5 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
                          j.queue_status === 'failed'
                            ? 'border-danger/30 bg-danger/10 text-danger'
                            : j.queue_status === 'running'
                              ? 'border-accent-500/30 bg-accent-500/10 text-accent-500'
                              : j.queue_status === 'queued'
                                ? 'border-warning/30 bg-warning-soft text-warning'
                                : 'border-success/30 bg-success-soft text-success',
                        )}
                      >
                        {j.queue_status}
                      </span>
                    )}
                    {j.expected_paused && j.expected_paused_reason ? (
                      <span className="text-text-secondary"> — {j.expected_paused_reason}</span>
                    ) : j.impact_when_paused ? (
                      <span className="text-text-secondary"> - {j.impact_when_paused}</span>
                    ) : null}
                    {j.queue_error && (
                      <span
                        className="block truncate text-caption text-danger"
                        title={j.queue_error}
                      >
                        Last error: {j.queue_error}
                      </span>
                    )}
                  </div>
                  {onRunNow && !j.expected_paused && (
                    <button
                      type="button"
                      onClick={() => handleRunNow(j.jobname)}
                      disabled={state === 'running'}
                      className="inline-flex items-center gap-1 rounded-sm border border-border bg-bg-surface px-2 py-0.5 text-[11px] font-medium text-text-primary transition-colors hover:border-accent-500 hover:text-accent-500 disabled:opacity-60"
                      title="Queue a background sync for this job."
                    >
                      {state === 'running' ? (
                        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      ) : state === 'ok' ? (
                        <CheckCircle2 className="h-3 w-3 text-success" aria-hidden />
                      ) : state === 'error' ? (
                        <AlertTriangle className="h-3 w-3 text-danger" aria-hidden />
                      ) : (
                        <Zap className="h-3 w-3" aria-hidden />
                      )}
                      Sync now
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <a
        href={settingsHref}
        className="inline-flex items-center gap-1 self-start text-caption font-medium text-accent-500 hover:underline"
      >
        Manage cron jobs <ArrowRight className="h-3 w-3" aria-hidden />
      </a>
    </section>
  );
}
